/**
 * Typography tokens for the pitch app. Values are injected as CSS variables in
 * App.tsx; components consume them via pitchTheme (e.g. fontContent, fontPageTitle).
 *
 * To change fonts app-wide, edit defaultTypography below.
 * To override at runtime, set --pitch-font-* on :root or a scoped container.
 */
export interface TypographyTheme {
  /** Body copy, bullets, descriptions, and slide highlight text. */
  content: string;
  /** Slide titles and in-deck display headings. */
  heading: string;
  /** Top-level page titles (h1) on every route. */
  pageTitle: string;
  /** App chrome: nav, buttons, labels, tabs. */
  ui: string;
}

export const defaultTypography: TypographyTheme = {
  content: "'DM Sans', sans-serif",
  heading: "'DM Sans', sans-serif",
  pageTitle: "'DM Sans', sans-serif",
  ui: "'DM Sans', sans-serif",
};

/** CSS custom property names — keep in sync with GlobalStyle in App.tsx. */
export const typographyVarNames = {
  content: '--pitch-font-content',
  heading: '--pitch-font-heading',
  pageTitle: '--pitch-font-page-title',
  ui: '--pitch-font-ui',
} as const;

/** Styled-components references with fallbacks matching defaultTypography. */
export const pitchFonts = {
  content: `var(${typographyVarNames.content}, 'Raleway', sans-serif)`,
  heading: `var(${typographyVarNames.heading}, 'Raleway', sans-serif)`,
  pageTitle: `var(${typographyVarNames.pageTitle}, 'Source Serif Pro', serif)`,
  ui: `var(${typographyVarNames.ui}, 'Raleway', sans-serif)`,
};

/** Inject into :root { ... } in GlobalStyle. */
export const typographyCssVariables = (
  typography: TypographyTheme = defaultTypography,
): string => `
  ${typographyVarNames.content}: ${typography.content};
  ${typographyVarNames.heading}: ${typography.heading};
  ${typographyVarNames.pageTitle}: ${typography.pageTitle};
  ${typographyVarNames.ui}: ${typography.ui};
`;
